"use client";

import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FlaskConical, Loader2, ShieldAlert, Info } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/components/ui/use-toast";
import { fetchSandbox, updateSandbox } from "@/state/sandbox/sandbox.slice";

// Each selectable mode and how its number is labelled.
const MODES = [
  { id: "off", title: "Off — show everything", blurb: "All sales, totals and reports show the real figures.", unit: null },
  { id: "count", title: "Show newest N sales", blurb: "Only the most recent N sales are visible anywhere.", unit: "sales", placeholder: "e.g. 25" },
  { id: "amount", title: "Show sales up to an amount", blurb: "Whole sales, newest first, until this cash total is reached.", unit: "GH₵", placeholder: "e.g. 1000" },
  { id: "percentage", title: "Show a percentage of sales", blurb: "Roughly this share of sales in any period.", unit: "%", placeholder: "e.g. 40" },
];

export default function SandboxSettingsPage() {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const state = useSelector((s) => s.sandbox);
  const { available, active, label, mode, value, updatedBy, updatedAt, loaded, isLoading, isSaving } = state;

  const [selMode, setSelMode] = useState("off");
  const [val, setVal] = useState("");

  useEffect(() => {
    dispatch(fetchSandbox());
  }, [dispatch]);

  // Sync form once config arrives.
  useEffect(() => {
    if (loaded) {
      setSelMode(mode || "off");
      setVal(mode && mode !== "off" ? String(value ?? "") : "");
    }
  }, [loaded, mode, value]);

  const active_mode = MODES.find((m) => m.id === selMode) || MODES[0];
  const needsValue = selMode !== "off";

  const save = async () => {
    const payload = { mode: selMode, value: needsValue ? Number(val) : 0 };
    if (needsValue && (!Number.isFinite(payload.value) || payload.value <= 0)) {
      toast({ variant: "destructive", title: "Enter a value", description: "The value must be greater than 0." });
      return;
    }
    if (selMode === "percentage" && payload.value > 100) {
      toast({ variant: "destructive", title: "Too high", description: "Percentage cannot exceed 100." });
      return;
    }
    try {
      const res = await dispatch(updateSandbox(payload)).unwrap();
      toast({ title: res.message || "Saved" });
    } catch (message) {
      toast({ variant: "destructive", title: "Couldn't save", description: String(message) });
    }
  };

  if (!loaded && isLoading) {
    return (
      <div className="flex h-64 items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-5 p-4">
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-amber-100 p-2 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300">
          <FlaskConical className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Sandbox — Sales Visibility</h1>
          <p className="text-sm text-muted-foreground">
            Limit how many sales are shown across the app for demos. The stored data is never changed.
          </p>
        </div>
      </div>

      {!available ? (
        <Card>
          <CardContent className="flex gap-3 p-5">
            <ShieldAlert className="h-5 w-5 shrink-0 text-muted-foreground" />
            <div className="space-y-1 text-sm">
              <p className="font-medium">Not available on this server</p>
              <p className="text-muted-foreground">
                This control only works on a sandbox server started with{" "}
                <code className="rounded bg-muted px-1">SANDBOX_MODE=true</code>. On production it is switched off and
                cannot be enabled — every screen always shows the real figures.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              While a limit is active, every sales page, the dashboard and reports show a banner saying the figures are
              simulated. This keeps demos honest — scaled numbers are never presented as the real totals.
            </p>
          </div>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">What should be visible</CardTitle>
              <CardDescription>
                {active ? `Currently active — ${label}.` : "Currently off — the app shows everything."}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <RadioGroup value={selMode} onValueChange={setSelMode} className="space-y-2">
                {MODES.map((m) => (
                  <label
                    key={m.id}
                    htmlFor={`mode-${m.id}`}
                    className={`flex cursor-pointer gap-3 rounded-md border p-3 transition-colors hover:bg-muted/50 ${
                      selMode === m.id ? "border-amber-400 bg-amber-50/60 dark:bg-amber-950/20" : ""
                    }`}
                  >
                    <RadioGroupItem value={m.id} id={`mode-${m.id}`} className="mt-0.5" />
                    <div className="space-y-0.5">
                      <p className="text-sm font-medium">{m.title}</p>
                      <p className="text-xs text-muted-foreground">{m.blurb}</p>
                    </div>
                  </label>
                ))}
              </RadioGroup>

              {needsValue && (
                <div className="space-y-1.5">
                  <Label htmlFor="sandbox_value">
                    {selMode === "count" ? "Number of sales" : selMode === "amount" ? "Cash limit (GH₵)" : "Percentage (%)"}
                  </Label>
                  <div className="flex items-center gap-2">
                    {active_mode.unit && selMode !== "count" && (
                      <span className="text-sm text-muted-foreground w-10">{active_mode.unit}</span>
                    )}
                    <Input
                      id="sandbox_value"
                      type="number"
                      inputMode="decimal"
                      min="0"
                      max={selMode === "percentage" ? 100 : undefined}
                      step={selMode === "count" ? 1 : "0.01"}
                      value={val}
                      onChange={(e) => setVal(e.target.value)}
                      placeholder={active_mode.placeholder}
                      className="max-w-[220px]"
                    />
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between border-t pt-4">
                <p className="text-xs text-muted-foreground">
                  {updatedAt ? `Last changed by ${updatedBy || "unknown"} on ${new Date(updatedAt).toLocaleString()}` : "Not set yet"}
                </p>
                <Button onClick={save} disabled={isSaving}>
                  {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {selMode === "off" ? "Turn off" : "Apply limit"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
